/**
 * Phase 2.13 — Operating & Regulatory Context (reviewer-confirmed).
 *
 * Generates an executive-friendly context block driven by REAL scope inputs:
 *   • Assessment Profile (sector, region, headquarters, parent, frameworks, notes)
 *   • Telecom Service Models selected (MNO / FiberCo / TowerCo / MVNO / ISP / DataCenter / Wholesale / NeutralHost / ...)
 *   • Infrastructure Components Present & Assessed (from reviewer scope)
 *   • Reviewer-entered methodology frameworks
 *
 * Phase 2.13 guarantees:
 *   – No region → regulator inference. Specific regulators (NIS2 / FCC / LGPD …)
 *     are NEVER asserted from region alone.
 *   – No hardcoded "core / transport / access / CDR / location data" claims for
 *     entities that did not select the corresponding service model or
 *     infrastructure components.
 *   – Frameworks are presented as "selected for alignment", not as legal
 *     guarantees.
 *
 * This module is presentation-only. It performs no scoring, finding, or
 * questionnaire logic.
 */

export interface TelecomContextInputs {
  orgName?: string | null;
  parentOrganization?: string | null;
  headquartersLocation?: string | null;
  region?: string | null;
  sector?: string | null;
  /** Selected methodology framework labels (canonical strings). */
  frameworks?: string[] | null;
  /** Free-form additional operating / regulatory context. */
  notes?: string | null;
  /** Legacy free-text methodology — used only when frameworks is empty. */
  legacyMethodology?: string | null;
  /** Real telecom service models selected on the assessment scope (or portfolio). */
  serviceModels?: string[] | null;
  /** Infrastructure components present (from reviewer scope). */
  componentsPresent?: string[] | null;
  /** Infrastructure components assessed (from reviewer scope). */
  componentsAssessed?: string[] | null;
  /** Reviewer-confirmation flag (Phase 2.13). */
  confirmed?: boolean | null;
  /** Phase 2.13.1 — reviewer-customized free text. */
  override?: string | null;
  /** Phase 2.13.1 — when true, render reviewer text in place of the generated summary/bullets. */
  overrideEnabled?: boolean | null;
}

export interface FrameworkBlock {
  framework: string;
  applicability: string;
  requirements: string;
  purpose: string;
}

export interface TelecomContextBlock {
  /** One-line, displayed under the heading. */
  summary: string;
  /** Operating-footprint bullets (sector/region/dependencies). */
  bullets: string[];
  /** Per-framework Applicability / Requirements / Purpose blocks. */
  frameworkBlocks: FrameworkBlock[];
  /** Optional appendix paragraph for additional notes. */
  notes?: string;
  /** Optional legacy methodology paragraph (only when no frameworks selected). */
  legacyMethodology?: string;
  /** Whether the telecom-specific framing was used (vs. neutral fallback). */
  telecomSpecific: boolean;
  /** Reviewer-confirmation flag (Phase 2.13). */
  confirmed: boolean;
  /** Phase 2.13.1 — reviewer free text (always returned for editing UI). */
  override: string;
  /** Phase 2.13.1 — when true the report renders override in place of generated body. */
  overrideEnabled: boolean;
  /** Phase 2.13.1 — generated default summary (used to pre-populate edit textarea). */
  generatedSummary: string;
  /** Phase 2.13.1 — generated default bullets (used to pre-populate edit textarea). */
  generatedBullets: string[];
}

export const METHODOLOGY_FRAMEWORK_OPTIONS = [
  "NIST CSF 2.0",
  "ISO/IEC 27001",
  "GDPR",
  "Local Data Protection Laws",
  "Telecom Regulatory Requirements",
  "World Bank / IFC Cyber Due Diligence Criteria",
  "Custom Methodology",
] as const;

export type MethodologyFramework = (typeof METHODOLOGY_FRAMEWORK_OPTIONS)[number];

const FRAMEWORK_CATALOG: Record<string, Omit<FrameworkBlock, "framework">> = {
  "NIST CSF 2.0": {
    applicability:
      "Selected as the primary maturity scoring lens across the six NIST CSF 2.0 functions (Govern, Identify, Protect, Detect, Respond, Recover).",
    requirements:
      "Outcome-based categories and subcategories covering governance, supply chain risk, identity, data protection, monitoring and recovery.",
    purpose:
      "Provides an internationally recognised baseline to benchmark cyber programme maturity and prioritise remediation.",
  },
  "ISO/IEC 27001": {
    applicability:
      "Selected where the organisation operates (or intends to operate) a formal Information Security Management System or seeks certification.",
    requirements:
      "Documented ISMS, risk assessment and treatment, Annex A control set, internal audit and management review.",
    purpose:
      "Supports a systematic, audit-ready security management approach aligned with international certification expectations.",
  },
  "GDPR": {
    applicability:
      "Selected for alignment where personal data of EU/UK data subjects may be processed. Actual applicability should be confirmed by counsel.",
    requirements:
      "Lawful basis for processing, data-subject rights, 72-hour breach notification, DPIAs for high-risk processing, and appropriate technical and organisational measures.",
    purpose:
      "Reduces regulatory exposure from cross-border personal data processing and supports data-subject rights.",
  },
  "Local Data Protection Laws": {
    applicability:
      "Selected for alignment with the national privacy regimes of the jurisdictions in which the organisation operates. Specific laws should be confirmed by counsel.",
    requirements:
      "Country-specific consent, localisation, breach reporting, cross-border transfer and supervisory authority obligations.",
    purpose:
      "Supports compliance with national privacy regimes and reduces exposure to local enforcement actions.",
  },
  "Telecom Regulatory Requirements": {
    applicability:
      "Selected for alignment with sector-specific obligations of licensed telecommunications operators in the relevant jurisdictions.",
    requirements:
      "Sector-specific expectations on network resilience, lawful interception, incident reporting to the regulator, subscriber data protection and continuity of critical services.",
    purpose:
      "Supports licence-to-operate and protection of critical communications infrastructure.",
  },
  "World Bank / IFC Cyber Due Diligence Criteria": {
    applicability:
      "Selected for alignment with World Bank Group / IFC cyber due-diligence expectations for investees and projects.",
    requirements:
      "Risk-based assessment of governance, third-party risk, data protection, incident response and resilience, with documented evidence and remediation plans.",
    purpose:
      "Provides investors with a consistent cyber risk view at deal screening, monitoring and exit.",
  },
  "Custom Methodology": {
    applicability:
      "Selected to tailor the assessment to engagement-specific scope or client context.",
    requirements:
      "A documented combination of standards, regulatory references and client-specific control expectations agreed at engagement start.",
    purpose:
      "Aligns the assessment with the client's specific risk context and stakeholder expectations.",
  },
};

// Phase 2.13: NO regulator inference from region. The only safe statement is
// neutral guidance asking the reviewer to confirm jurisdiction-specific
// obligations.
const NEUTRAL_REGULATORY_LANGUAGE =
  "Applicable regulatory expectations should be confirmed based on the entity's operating jurisdictions, licences, and services offered.";

function normList(arr?: string[] | null): string[] {
  if (!Array.isArray(arr)) return [];
  return arr.map((v) => (v ?? "").toString().trim()).filter(Boolean);
}

function hasModel(models: string[], ...needles: string[]): boolean {
  const low = models.map((m) => m.toLowerCase());
  return needles.some((n) => low.some((m) => m.includes(n.toLowerCase())));
}

function hasComponent(components: string[], ...needles: string[]): boolean {
  const low = components.map((m) => m.toLowerCase());
  return needles.some((n) => low.some((m) => m.includes(n.toLowerCase())));
}

function locationPhrase(input: TelecomContextInputs): string {
  const parts: string[] = [];
  if (input.headquartersLocation?.trim()) parts.push(input.headquartersLocation.trim());
  if (input.region?.trim() && input.region.trim() !== input.headquartersLocation?.trim()) {
    parts.push(input.region.trim());
  }
  if (parts.length === 0) return "its operating geography";
  return parts.join(", ");
}

function isTelecomSector(sector?: string | null): boolean {
  if (!sector) return false;
  const s = sector.toLowerCase();
  return s.includes("telecom") || s.includes("mobile network") || s.includes("isp") ||
         s.includes("fiber") || s.includes("tower") || s.includes("data center") ||
         s.includes("communication");
}

/**
 * Build scope-aware operating bullets from real selections.
 * Returns an empty array if there is nothing safe to assert.
 */
function buildScopeBullets(
  serviceModels: string[],
  componentsPresent: string[],
  componentsAssessed: string[],
): string[] {
  const bullets: string[] = [];
  const components = Array.from(new Set([...componentsPresent, ...componentsAssessed]));

  if (serviceModels.length === 0 && components.length === 0) {
    return bullets;
  }

  if (serviceModels.length > 0) {
    bullets.push(
      `Service model in scope: ${serviceModels.join(", ")}. Operating exposure is framed by these selections and the supporting infrastructure scope.`,
    );
  }

  // MNO — only reference RAN/Core/Transport/subscriber exposure if components support it.
  if (hasModel(serviceModels, "MNO", "Mobile Network Operator")) {
    const ranOrAccess = hasComponent(components, "RAN", "Access", "Radio");
    const coreRef = hasComponent(components, "Core", "Packet Core", "EPC", "5GC");
    const transportRef = hasComponent(components, "Transport", "Backhaul");
    const subRef = hasComponent(components, "Subscriber", "HLR", "HSS", "CDR", "Billing");
    const parts: string[] = [];
    if (ranOrAccess) parts.push("access / radio");
    if (coreRef) parts.push("core network");
    if (transportRef) parts.push("transport / backhaul");
    if (parts.length > 0) {
      bullets.push(
        `Mobile network operations: scope includes ${parts.join(", ")} infrastructure — control gaps in these layers can cascade to service availability.`,
      );
    }
    if (subRef) {
      bullets.push(
        "Subscriber and operational data handled within scope (e.g. subscriber records, billing or call/usage detail records) warrants proportionate confidentiality and integrity controls.",
      );
    }
  }

  // MVNO — explicitly avoid claiming RAN/Core ownership unless components selected.
  if (hasModel(serviceModels, "MVNO")) {
    const ownsCoreOrRan = hasComponent(components, "RAN", "Core", "Packet Core");
    if (!ownsCoreOrRan) {
      bullets.push(
        "Mobile virtual network operations: scope is centred on BSS/OSS, subscriber management and the host-network interface rather than owned RAN or core infrastructure.",
      );
    }
  }

  // FiberCo / wholesale fixed
  if (hasModel(serviceModels, "FiberCo", "Fixed", "Wholesale Carrier")) {
    bullets.push(
      "Fixed / wholesale transport operations: route diversity, point-of-presence and meet-me-room resilience, and fibre-route integrity are central exposure points.",
    );
  }

  // TowerCo / passive infra
  if (hasModel(serviceModels, "TowerCo", "Passive Infrastructure", "Neutral Host")) {
    bullets.push(
      "Passive / shared infrastructure operations: physical site access, power and environmental resilience, and dependencies of co-locating tenants drive the effective risk surface.",
    );
  }

  // ISP
  if (hasModel(serviceModels, "ISP", "Internet Service Provider")) {
    bullets.push(
      "Internet service operations: customer connectivity, peering / transit dependencies and DDoS exposure should be reflected in resilience controls.",
    );
  }

  // Data Center
  if (hasModel(serviceModels, "Data Center", "DataCenter", "Colocation")) {
    bullets.push(
      "Data centre / colocation operations: physical security, power and cooling resilience, and customer-tenancy isolation drive the effective risk surface.",
    );
  }

  // Components without an explicit service-model entry — fall back to generic
  // scope statement so the reader knows where exposure is concentrated.
  if (bullets.length <= 1 && components.length > 0) {
    bullets.push(
      `Infrastructure components in scope: ${components.join(", ")}.`,
    );
  }

  return bullets;
}

export function buildTelecomContextBlock(input: TelecomContextInputs): TelecomContextBlock {
  const org = (input.orgName || input.parentOrganization || "The organisation").trim();
  const where = locationPhrase(input);
  const telecom = isTelecomSector(input.sector);

  const serviceModels = normList(input.serviceModels);
  const componentsPresent = normList(input.componentsPresent);
  const componentsAssessed = normList(input.componentsAssessed);

  const frameworks = normList(input.frameworks);
  const frameworkBlocks: FrameworkBlock[] = frameworks
    .map((framework) => {
      const tpl = FRAMEWORK_CATALOG[framework];
      if (!tpl) return null;
      return { framework, ...tpl };
    })
    .filter((b): b is FrameworkBlock => b !== null);

  const notes = (input.notes || "").trim() || undefined;
  const legacy = (input.legacyMethodology || "").trim();
  const legacyMethodology =
    frameworkBlocks.length === 0 && legacy ? legacy : undefined;
  const confirmed = !!input.confirmed;

  const scopeBullets = buildScopeBullets(serviceModels, componentsPresent, componentsAssessed);

  // Neutral, jurisdiction-agnostic regulatory framing (Phase 2.13).
  const regulatoryBullet = NEUTRAL_REGULATORY_LANGUAGE;

  // Compose summary safely. We never assert specific operating models or
  // regulators unless the reviewer selected them.
  const summarySector =
    telecom
      ? "in the telecommunications and digital-infrastructure sector"
      : "as a digital-infrastructure operator";

  const summary =
    serviceModels.length > 0
      ? `${org} operates ${summarySector} in ${where}; the cyber risk framing reflects the selected service model(s) — ${serviceModels.join(", ")} — and the infrastructure scope confirmed for this engagement.`
      : `${org} operates ${summarySector} in ${where}. The cyber risk framing below is drawn from available assessment profile and scope inputs; service-model and infrastructure scope should be confirmed before the report is finalised.`;

  const bullets: string[] = [];
  if (scopeBullets.length > 0) {
    bullets.push(...scopeBullets);
  } else {
    bullets.push(
      "Operating environment: cyber exposure is shaped by the services offered, the data handled, and the third-party ecosystem supporting operations. Scope-specific framing will appear once telecom service model and infrastructure components are recorded.",
    );
  }
  bullets.push(
    "Third-party and vendor ecosystem: managed services, OEM equipment and outsourced platforms materially influence the effective control environment and should be reflected in supplier governance.",
  );
  bullets.push(regulatoryBullet);

  const override = (input.override || "").toString();
  const overrideEnabled = !!input.overrideEnabled && override.trim().length > 0;

  // When override is active, replace summary + bullets with reviewer text.
  const effectiveSummary = overrideEnabled ? override.trim() : summary;
  const effectiveBullets = overrideEnabled ? [] : bullets;

  return {
    telecomSpecific: telecom,
    summary: effectiveSummary,
    bullets: effectiveBullets,
    frameworkBlocks,
    notes,
    legacyMethodology,
    confirmed,
    override,
    overrideEnabled,
    generatedSummary: summary,
    generatedBullets: bullets,
  };
}

